import { GRAY_1000, GRAY_800, GRAY_900, InterVariableFontFamily, WHITE } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { ExpandedVisualType } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import React from 'react'
import { useTranslation } from 'react-i18next'

/** Props for the VisualTypeSwitcher component. */
type Props = {
  /** OnClick action to switch expanded visual type */
  switchExpandedVisualType?: (type: ExpandedVisualType) => void
  /** Current expanded visual type: original or edited */
  expandedVisualType?: ExpandedVisualType
  /** Whether the switcher is disabled */
  disabled?: boolean
  /**
   * Lab SDK engine, once a result exists: the switch reads Before | Compare | After instead of
   * Editing | Original, and the middle segment opens the before/after comparison.
   */
  showCompare?: boolean
  /** Whether the comparison view is the active segment */
  isCompareActive?: boolean
  /** Called when the Compare segment is selected */
  onCompare?: () => void
}

/**
 * @component
 * Visual Type Switcher component
 *
 * @example
 * <VisualTypeSwitcher />
 */
export const VisualTypeSwitcher: React.FC<Props> = ({
  switchExpandedVisualType,
  expandedVisualType,
  disabled = false,
  showCompare = false,
  isCompareActive = false,
  onCompare,
}) => {
  const { t } = useTranslation(['gallery', 'photo_studio_editing'])

  const segments: Array<{ key: string, label: string, isActive: boolean, onClick: () => void }> = showCompare
    ? [
      { key: 'before', label: t('photo_studio_editing:lab.compare.before'), isActive: !isCompareActive && expandedVisualType === ExpandedVisualType.ORIGINAL, onClick: () => switchExpandedVisualType?.(ExpandedVisualType.ORIGINAL) },
      { key: 'compare', label: t('photo_studio_editing:lab.compare.compare'), isActive: isCompareActive, onClick: () => onCompare?.() },
      { key: 'after', label: t('photo_studio_editing:lab.compare.after'), isActive: !isCompareActive && expandedVisualType === ExpandedVisualType.EDITED, onClick: () => switchExpandedVisualType?.(ExpandedVisualType.EDITED) },
    ]
    : [
      { key: 'editing', label: t('gallery:editing'), isActive: expandedVisualType === ExpandedVisualType.EDITED, onClick: () => switchExpandedVisualType?.(ExpandedVisualType.EDITED) },
      { key: 'original', label: t('gallery:original'), isActive: expandedVisualType === ExpandedVisualType.ORIGINAL, onClick: () => switchExpandedVisualType?.(ExpandedVisualType.ORIGINAL) },
    ]

  return (
    <Stack
      gap=".4rem"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      padding=".2rem"
      height="3.2rem"
      sx={{
        border: `1px solid ${GRAY_900}`,
        background: GRAY_1000,
        borderRadius: '10rem',
        boxSizing: 'border-box',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'default',
        pointerEvents: disabled ? 'none' : 'auto',
        'span': {
          fontSize: '1.4rem !important', // Override pintura font size — same size as the footer's Cancel / Save result
          fontFamily: `${InterVariableFontFamily} !important`, // Override pintura font family
        }
      }}
    >
      {segments.map((segment) => (
        <Stack
          key={segment.key}
          height="100%"
          borderRadius="10rem"
          padding=".2rem 1.6rem"
          justifyContent="center"
          onClick={segment.onClick}
          sx={{
            backgroundColor: segment.isActive ? GRAY_800 : 'transparent',
            cursor: 'pointer',
            boxSizing: 'border-box'
          }}
        >
          <Typography variant="text-sm" fontWeight={segment.isActive ? 550 : 350} sx={{ color: WHITE }}>
            {segment.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}
